import { z } from "zod";

const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const HEX_COLOR_REGEX = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;


export const getRequestSiteConfigSchema = (provider = "stripe", isFranchise = false, isFirstTab = true) =>
  z
    .object({})
    .passthrough()
    .superRefine((data, ctx) => {
      const err = (path, message) => ctx.addIssue({ code: "custom", message, path });

      if (!data.appIcon) err(["appIcon"], "App icon required");
      if (isFirstTab || !isFranchise) {
        if (!data.favicon) err(["favicon"], "Favicon required");
      }

      const ac = data.appConfig;

      if (isFranchise && !ac?.siteBanner) err(["appConfig", "siteBanner"], "Site banner required");
      if (isFranchise && isFirstTab && !ac?.companyName?.trim()) err(["appConfig", "companyName"], "Company name required");
      if (!ac?.SiteName?.trim()) err(["appConfig", "SiteName"], "Site name required");
      if (!ac?.timeZone?.trim()) err(["appConfig", "timeZone"], "Timezone required");

      if (!ac?.Email?.trim()) {
        err(["appConfig", "Email"], "Email required");
      } else if (!EMAIL_REGEX.test(ac.Email.trim())) {
        err(["appConfig", "Email"], "Invalid email");
      }

      if (!ac?.colors?.primaryColor?.trim()) {
        err(["appConfig", "colors", "primaryColor"], "Primary color required");
      } else if (!HEX_COLOR_REGEX.test(ac.colors.primaryColor.trim())) {
        err(["appConfig", "colors", "primaryColor"], "Primary color must be a valid hex color");
      }

      if (!ac?.colors?.secondaryColor?.trim()) {
        err(["appConfig", "colors", "secondaryColor"], "Secondary color required");
      } else if (!HEX_COLOR_REGEX.test(ac.colors.secondaryColor.trim())) {
        err(["appConfig", "colors", "secondaryColor"], "Secondary color must be a valid hex color");
      }

      const loc = ac?.Location;
      if (!loc?.latitude || loc.latitude === 0)
        err(["appConfig", "Location", "latitude"], "Latitude required");
      if (!loc?.latitudeDelta || loc.latitudeDelta === 0)
        err(["appConfig", "Location", "latitudeDelta"], "Latitude delta required");
      if (!loc?.longitude || loc.longitude === 0)
        err(["appConfig", "Location", "longitude"], "Longitude required");
      if (!loc?.longitudeDelta || loc.longitudeDelta === 0)
        err(["appConfig", "Location", "longitudeDelta"], "Longitude delta required");

      const ms = data.mailSettings;
      if (!ms?.service?.trim()) err(["mailSettings", "service"], "Service required");
      if (!ms?.mailPort || ms.mailPort === 0)
        err(["mailSettings", "mailPort"], "Mail port required");
      if (!ms?.userName?.trim()) err(["mailSettings", "userName"], "User name required");
      if (!ms?.resetPasswordLink?.trim())
        err(["mailSettings", "resetPasswordLink"], "Reset password link required");

      if (Array.isArray(data.languages)) {
        data.languages.forEach((lang, i) => {
          if (!lang?.code?.trim()) err(["languages", i, "code"], "Code required");
          if (!lang?.key?.trim()) err(["languages", i, "key"], "Key required");
          if (!lang?.name?.trim()) err(["languages", i, "name"], "Name required");
        });
      }

      if (provider === "stripe") {
        const sc = data.stripeConfig;
        if (!sc?.country?.trim()) err(["stripeConfig", "country"], "Country required");
        if (!sc?.feeMoney && sc?.feeMoney !== 0)
          err(["stripeConfig", "feeMoney"], "Fee money required");
        if (sc?.feeMoney === 0) err(["stripeConfig", "feeMoney"], "Fee money required");
        if (!sc?.publishableKey?.trim())
          err(["stripeConfig", "publishableKey"], "Publishable key required");
        if (!sc?.taxCode?.trim()) err(["stripeConfig", "taxCode"], "Tax code required");
        if (
          sc?.isProcessingFeeEnabled &&
          (!sc?.processingFeePercentage || sc.processingFeePercentage === 0)
        )
          err(["stripeConfig", "processingFeePercentage"], "Processing fee percentage required");
      } else if (provider === "paystack") {
        const ps = data.payStack;
        if (!ps?.apiUrl?.trim()) err(["payStack", "apiUrl"], "API URL required");
        if (!ps?.feeMoney && ps?.feeMoney !== 0)
          err(["payStack", "feeMoney"], "Fee money required");
        if (ps?.feeMoney === 0) err(["payStack", "feeMoney"], "Fee money required");
      } else if (provider === "cybersource") {
        const cs = data.cyberSourceConfig;
        if (!cs?.currency?.trim()) err(["cyberSourceConfig", "cyberSourceCurrency"], "Currency required");
        if (!cs?.environment?.trim()) err(["cyberSourceConfig", "cyberSourceEnvironment"], "Environment required");
      } else if (provider === "square") {
        const sc = data.squareConfig;
        if (
          sc?.appFeePercentage === undefined ||
          isNaN(sc?.appFeePercentage)
        )
          err(["squareConfig", "appFeePercentage"], "App fee percentage required");
        if (!sc?.appId?.trim()) err(["squareConfig", "appId"], "App ID required");
        if (!sc?.basePath?.trim()) err(["squareConfig", "basePath"], "Base path required");
        if (!sc?.country?.trim()) err(["squareConfig", "country"], "Country required");
        if (!sc?.environment?.trim()) err(["squareConfig", "environment"], "Environment required");
        if (!sc?.expiresAt?.trim()) err(["squareConfig", "expiresAt"], "Expires at required");
        if (!sc?.redirectUri?.trim()) err(["squareConfig", "redirectUri"], "Redirect URI required");
      }
    });

export const parseRequestSiteConfigErrors = (data, provider = "stripe", isFranchise = false, isFirstTab = true) => {
  const schema = getRequestSiteConfigSchema(provider, isFranchise, isFirstTab);
  const result = schema.safeParse(data);
  if (result.success) return {};
  const errors = {};
  for (const issue of result.error.issues) {
    const key = issue.path.join(".");
    if (!errors[key]) errors[key] = issue.message;
  }
  return errors;
};
